'use client'

import { Textarea, TextareaProps } from '@headlessui/react'
import clsx from 'clsx'
import type { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import { forwardRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import InputAddon, { InputAddonProps } from '../InputAddon/InputAddon'
import InputError from '../InputError/InputError'
import InputHint from '../InputHint/InputHint'
import InputLabel from '../InputLabel/InputLabel'

interface TextAreaInputStandaloneProps
  extends InputAddonProps,
    FormFieldCommonProps,
    Omit<TextareaProps, 'onChange' | 'className' | 'defaultValue'> {
  /**
   * Input placeholder text
   */
  placeholder?: string
  /**
   * Callback fired when input value changes
   */
}

export interface TextAreaInputProps
  extends ControlledFormField,
    TextAreaInputStandaloneProps {}

// Standalone component that can be used outside react-hook-form's context
export const TextAreaInputStandalone = forwardRef<
  HTMLTextAreaElement,
  TextAreaInputStandaloneProps
>(function TextAreaInputStandaloneWithRef(
  {
    label,
    name,
    id = name,
    className,
    error,
    disabled,
    hint,
    placeholder,
    ...props
  }: TextAreaInputStandaloneProps,
  ref,
) {
  const [isEnabled, setIsEnabled] = useState(
    'conditionalEdit' in props && props.conditionalEdit ? false : !disabled,
  )

  return (
    <div className={clsx(`text-input`, className)}>
      <div className="flex items-baseline justify-between">
        {label && <InputLabel htmlFor={id}>{label}</InputLabel>}
        <InputAddon
          {...props}
          {...('conditionalEdit' in props && props.conditionalEdit
            ? { onEdit: setIsEnabled }
            : {})}
        />
      </div>
      <Textarea
        className={clsx(
          'w-full type-body-8 text-black rounded-lg h-[9.6875rem] flex items-center p-4 my-2 transition-shadow resize-none align-top',
          'disabled:bg-neutral-200 disabled:text-neutral-600 disabled:shadow-input-disabled disabled:cursor-not-allowed',
          'focus:shadow-input-focus focus:outline-none',
          'hover:shadow-input-active',
          error ? '!shadow-input-error' : 'shadow-input',
        )}
        placeholder={placeholder}
        disabled={!isEnabled}
        name={name}
        id={id}
        ref={ref}
        {...props}
      />
      <InputHint>{hint}</InputHint>
      <InputError>{error}</InputError>
    </div>
  )
})

// Controlled form element that is automatically registered to react-hook-form
export default function TextAreaInput({
  name,
  control,
  rules,
  defaultValue,
  disabled,
  ...props
}: TextAreaInputProps) {
  return (
    <Controller
      name={name || ''}
      defaultValue={defaultValue}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        return (
          <TextAreaInputStandalone
            {...props}
            {...field}
            disabled={disabled}
            error={fieldState.error?.message}
            onBlur={(e) => {
              field.onBlur()
              props.onBlur?.(e)
            }}
          />
        )
      }}
    />
  )
}
